import { useEffect, useRef } from "react";
import { useLocation, useSearchParams } from "react-router-dom";

import { SiteShell } from "@/components/site/SiteShell";
import { CalculatorHero } from "@/components/calculator/CalculatorHero";
import { FinanceRouting } from "@/components/calculator/FinanceRouting";
import { GeneralFallbackCta } from "@/components/calculator/GeneralFallbackCta";
import { PricingWizard } from "@/components/calculator/PricingWizard";
import { ServicePicker } from "@/components/calculator/ServicePicker";
import { WelcomeBackBanner } from "@/components/calculator/WelcomeBackBanner";
import { useCalculatorFlow } from "@/components/calculator/useCalculatorFlow";
import { FallbackPanel } from "@/components/fallback/FallbackPanel";
import { QuoteReveal } from "@/components/quote/QuoteReveal";
import { serviceKeyFromSlug } from "@/lib/landingLinks";

/**
 * Open the wizard on the service an advert pointed at.
 *
 * The landing pages link here with `?service=bookkeeping` and friends. This
 * only presses the same button the visitor would have pressed themselves, and
 * steps aside entirely if they have a saved quote waiting or have already
 * started - nothing about pricing or the questions changes.
 */
function useServiceFromQuery(flow) {
  const [params] = useSearchParams();
  const requested = serviceKeyFromSlug(params.get("service"));
  const applied = useRef(false);

  useEffect(() => {
    if (applied.current || !requested) return;
    if (flow.bootstrapping || flow.welcomeBack) return;
    if (flow.service || flow.phase !== "choose") return;
    applied.current = true;
    flow.selectService(requested);
    // `flow` is rebuilt every render, so the ref above - not the dependency
    // list - is what keeps this to a single selection.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requested, flow.bootstrapping, flow.welcomeBack, flow.service, flow.phase]);
}

/**
 * The public pricing calculator.
 *
 * Four phases live in the one flow section - choose, wizard, fallback, quote -
 * and `useCalculatorFlow` owns which one is showing and every move between
 * them. This file is only the page furniture around it.
 */
export default function PricingCalculatorPage() {
  const flow = useCalculatorFlow();
  const { search } = useLocation();
  useServiceFromQuery(flow);

  return (
    <SiteShell testId="pricing-calculator-page">
      <CalculatorHero />

      {flow.welcomeBack && flow.phase === "choose" ? (
        <WelcomeBackBanner
          session={flow.welcomeBack}
          onContinue={flow.resumeSaved}
          onStartOver={flow.startOver}
        />
      ) : null}

      <ServicePicker
        service={flow.service}
        bootstrapping={flow.bootstrapping}
        onSelect={flow.selectService}
      />

      <section ref={flow.wizardRef} className="mx-auto max-w-6xl scroll-mt-32 px-4 pt-12 sm:px-6">
        {flow.phase === "choose" ? (
          <div className="tt-card border-dashed p-8 text-center">
            <p className="text-sm text-muted-foreground">
              Pick one of the three services above to get started — it takes about two minutes.
            </p>
          </div>
        ) : null}

        {flow.phase === "wizard" && flow.step ? <PricingWizard flow={flow} /> : null}

        {flow.phase === "routing" && flow.financeOffer ? (
          <FinanceRouting
            search={search}
            onContinue={flow.continueWithBookkeeping}
            onBack={flow.reconsiderTurnover}
          />
        ) : null}

        {flow.phase === "fallback" && flow.fallback ? (
          <div className="mx-auto max-w-3xl">
            <FallbackPanel
              headline={flow.fallback.headline}
              body={flow.fallback.body}
              reasons={flow.fallback.reasons}
              sessionId={flow.sessionId}
              service={flow.service}
              answers={flow.answers}
              onBack={flow.leaveFallback}
            />
          </div>
        ) : null}

        {flow.phase === "quote" && flow.quoteResult ? (
          <div className="mx-auto max-w-3xl">
            <QuoteReveal result={flow.quoteResult} onStartOver={flow.startOver} />
          </div>
        ) : null}
      </section>

      <GeneralFallbackCta onContact={flow.openGeneralFallback} />
    </SiteShell>
  );
}
